import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, PaperPlaneTilt, CircleNotch, Question } from "@phosphor-icons/react";

const SUBJECTS = ["Technical Fault", "Feature Request"];

export default function HelpModal({ open, onClose }) {
  const { user } = useAuth();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const send = async () => {
    if (!subject) return toast.error("Please choose a subject");
    setBusy(true);
    try {
      await api.post("/support/help", { subject, message });
      toast.success("Message sent — our team will be in touch.");
      setSubject(""); setMessage("");
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const lockedCls = "w-full h-12 px-4 rounded-md border border-border bg-secondary text-muted-foreground";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="help-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-testid="help-backdrop" />
      <div className="relative w-full max-w-md rounded-[16px] bg-white shadow-2xl p-6 animate-fade-up">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-black text-xl text-[#2E2E2E] flex items-center gap-2">
            <Question size={22} weight="bold" className="text-[#3BB54A]" /> Help &amp; Support
          </h2>
          <button onClick={onClose} data-testid="help-close-button" className="p-1 text-muted-foreground hover:text-[#2E2E2E]">
            <X size={22} weight="bold" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-semibold mb-1.5 text-[#2E2E2E]">Name</label>
            <input value={user?.name || ""} disabled readOnly data-testid="help-name-input" className={lockedCls} />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1.5 text-[#2E2E2E]">Email</label>
            <input value={user?.email || ""} disabled readOnly data-testid="help-email-input" className={lockedCls} />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1.5 text-[#2E2E2E]">Subject</label>
            <select value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="help-subject-select"
              className="w-full h-12 px-3 rounded-md border border-border bg-white focus:outline-none focus:ring-2 focus:ring-[#3BB54A]">
              <option value="" disabled>Choose a subject…</option>
              {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1.5 text-[#2E2E2E]">Message</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="help-message-input"
              placeholder="Tell us what's going on…"
              className="w-full px-4 py-3 rounded-md border border-border bg-white focus:outline-none focus:ring-2 focus:ring-[#3BB54A] resize-none" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={onClose} data-testid="help-cancel-button"
            className="h-11 px-5 rounded-full border border-border text-[#2E2E2E] font-semibold hover:bg-secondary transition-colors">
            Cancel
          </button>
          <button onClick={send} disabled={busy} data-testid="help-send-button"
            className="h-11 px-6 rounded-full bg-[#3BB54A] text-white font-bold flex items-center gap-2 disabled:opacity-60 hover:-translate-y-0.5 transition-transform">
            {busy ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="fill" />} Send
          </button>
        </div>
      </div>
    </div>
  );
}
